import { type RouterOutput, trpc } from '@/config/trpc'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSidebarStore } from '@/stores/sidebarStore'
import { useNodeHistoryStore } from '@/stores/nodeHistoryStore'
import { WorkspaceEditorCanvas } from '@/components/workspace/workspace-editor/workspace-editor-canvas'
import { AspectRatioCustomInput } from '@/components/workspace/workspace-editor/aspect-ratio-custom-input'
import { useNodeStore, nodeStore } from '@/stores/nodeStore'
import { nodeClipboardStore } from '@/stores/nodeClipboardStore'
import { toast } from 'sonner'
import { blobToBase64 } from '@/lib/editor'
import { useCanvasStore } from '@/stores/canvasStore'
import { WorkspaceEditorPromptBox } from './workspace-editor-promptbox'
import { WorkspaceEditorMenuGeneral } from './workspace-editor-menu/workspace-editor-menu-general'
import { calculateCanvasSize } from '@/lib/editor'
import { PRESET_ASPECT_RATIOS } from '@backend/lib/util/aspectRatio'
import {
  CircleIcon,
  RectangleIcon,
  TriangleIcon,
  CheckMarkIcon,
  LoadingIcon,
} from '@/components/ui/icons/icons-new'
import { WorkspaceEditorBottomBar } from './workspace-editor-bottom-bar'
import { WorkspaceEditorPopupNew } from './workspace-editor-popup/workspace-editor-popup-new'
import { WorkspaceEditorPopupEdit } from './workspace-editor-popup/workspace-editor-popup-edit'
import { Button } from '@/components/ui/shadcn/button'
import { isPlaceholderFile } from '@backend/lib/db/typeRoots/fileWithUrl'

import { WorkspaceEditorMenuText } from './workspace-editor-menu/workspace-editor-menu-text'
import {
  EditorContextProvider,
  useEditorContext,
} from '@/components/modules/CanvasEditor/lexical/EditorContext'
import { applyStyleToSelection } from '@/components/modules/CanvasEditor/lexical/applyStyle'

const RUN_STYLE_KEYS = new Set<string>([
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'fontColor',
])
import { WorkspaceEditorMenuImage } from './workspace-editor-menu/workspace-editor-menu-image'
import { WorkspaceEditorMenuShape } from './workspace-editor-menu/workspace-editor-menu-shape'
import { useFontsLoader } from '@/hooks/editor/useFontLoader'
import { useCustomFonts } from '@/hooks/editor/useCustomFonts'
import { moodboardCustomFonts } from '@/lib/fontFamily'
import { useKeyBindings } from '@/hooks/useKeyBindings'
import {
  type ImageOptions,
  type LayoutNode,
  type ShapeOptions,
  type TextOptions,
  getEffectiveTextStyle,
} from '@backend/lib/db/typeRoots/layoutNode'
import { type Color } from '@backend/lib/db/typeRoots/color'
import { type ImageModel } from '@backend/lib/ai/functions/generateImage'
import { type GenerateImageOptions } from './workspace-editor-popup/types'
import { type Role } from '@/pages/workspace-page/workspace-page'
import { uploadToS3 } from '@/lib/utils'
import { stampCanvasProvenance } from '@/lib/canvasProvenance'
import { captureError } from '@/lib/errors'
import { type WorkspaceMedia, markAiEdited } from '@backend/lib/db/typeRoots/workspaceMedia'
import { UpgradeModal } from '@/components/upgrade-modal'

const ASPECT_RATIOS = PRESET_ASPECT_RATIOS.map((key) => ({ key, label: key }))

export type Workspace = RouterOutput['workspace']['get']

type WorkspaceEditorProps = {
  workspace: Workspace
  workspaceMedia: WorkspaceMedia[]
  mediaIndex: number
  setWorkspaceMedia: (media: WorkspaceMedia[]) => void
  onEditingDone: () => void
  onCancel: () => void
  autoOpenImageDialog?: boolean
}

export function WorkspaceEditor(props: WorkspaceEditorProps) {
  // The provider has to wrap the whole tree so the in-canvas Lexical editor
  // and the contextual top toolbar share the same selection-state channel.
  return (
    <EditorContextProvider>
      <WorkspaceEditorBody {...props} />
    </EditorContextProvider>
  )
}

function WorkspaceEditorBody({
  workspace,
  workspaceMedia,
  mediaIndex,
  setWorkspaceMedia,
  onEditingDone,
  onCancel,
  autoOpenImageDialog = false,
}: {
  workspace: Workspace
  workspaceMedia: WorkspaceMedia[]
  mediaIndex: number
  setWorkspaceMedia: (media: WorkspaceMedia[]) => void
  onEditingDone: () => void
  onCancel: () => void
  autoOpenImageDialog?: boolean
}) {
  //  refs
  const panAndZoomRef = useRef<HTMLDivElement>(null)
  const workspaceContainerRef = useRef<HTMLDivElement>(null)
  const quickMenuRef = useRef<HTMLDivElement>(null)

  // hooks

  const { historyUndo, historyRedo, canHistoryUndo, canHistoryRedo } = useNodeHistoryStore()
  const { isOpen } = useSidebarStore()
  const { capture } = useCanvasStore()
  const {
    nodes,
    selectedNodeIndex,
    selectedNodeIndices,
    selectedNode,
    selectedNodes,
    setNodes,
    updateNode,
    updateSelectedNodes,
    addNode,
    duplicateNode,
    deleteNode,
    deleteSelectedNodes,
    setSelectedNodeIndex,
    setSelectedNodeIndices,
    groupSelectedNodes,
    ungroupSelectedNodes,
  } = useNodeStore()
  const { fonts } = useFontsLoader()

  // keyboard bindings - using lazy evaluation since handlers are defined later
  useKeyBindings(selectedNodeIndex !== -1, {
    delete: () => {
      if (selectedNodeIndices.length === 0) return
      if (selectedNodeIndices.length > 1) {
        deleteSelectedNodes({ history: true })
      } else {
        // A locked layer is protected from accidental Backspace/Delete.
        if (selectedNode?.locked) return
        deleteNode(selectedNodeIndex, { history: true })
      }
    },
    toggleGroup: () => {
      if (selectedNodeIndices.length < 2) return
      // Check if all selected nodes are already in the same group
      const currentSelectedNodes = selectedNodeIndices.map((i) => nodes[i]).filter(Boolean)
      const areAllGrouped =
        currentSelectedNodes.length >= 2 &&
        currentSelectedNodes.every(
          (n) => n?.groupId && n.groupId === currentSelectedNodes[0]?.groupId,
        )

      if (areAllGrouped) {
        ungroupSelectedNodes({ history: true })
      } else {
        groupSelectedNodes({ history: true })
      }
    },
    selectAll: () => {
      setSelectedNodeIndices(nodes.map((_, i) => i))
    },
    nudge: (dx, dy) => {
      // Locked layers stay put, matching drag behaviour.
      const movable = selectedNodeIndices.filter((i) => !nodes[i]?.locked)
      if (movable.length === 0) return
      movable.forEach((i, k) => {
        const node = nodes[i]
        if (!node) return
        // Record one history step per keypress, on the last node so the
        // captured state already includes every move. Persistence happens on
        // commit (handleCanvasEditingDone), not per keystroke.
        const isLast = k === movable.length - 1
        updateNode(i, { x: node.x + dx, y: node.y + dy }, isLast ? { history: true } : undefined)
      })
    },
  })

  // state

  const [isWorkspaceSaving, setIsWorkspaceSaving] = useState(false)
  const [overlayOpacity, setOverlayOpacity] = useState(0.12)

  const [panAndZoom, setPanAndZoom] = useState({ scale: 1, x: 0, y: 0 })
  const [panningStart, setPanningStart] = useState<{ x: number; y: number }>()

  // Canvas state from child
  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number }>()
  const [canvasAspect, setCanvasAspect] = useState<string>()

  // Device pixel ratio — the exported/captured canvas is logical size × dpr, so
  // the resolution badge reflects the actual output for the current device.
  // It can change when the window moves between monitors of different scaling.
  const [dpr, setDpr] = useState(() => window.devicePixelRatio || 1)
  useEffect(() => {
    const update = () => setDpr(window.devicePixelRatio || 1)
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  // File input refs for image upload and replace
  const fileInputRef = useRef<HTMLInputElement>(null)
  const replaceFileInputRef = useRef<HTMLInputElement>(null)

  // Prompt box state
  const [imageModel, setImageModel] = useState<ImageModel>('flux')
  const [isGenerateImagePressed, setIsGenerateImagePressed] = useState(false)

  const inputButtonRef = useRef<HTMLDivElement>(null)

  const [isGeneratingImage, setIsGeneratingImage] = useState(false)

  const [isImageDialogOpen, setIsImageDialogOpen] = useState(autoOpenImageDialog)
  const [isImageEditDialogOpen, setIsImageEditDialogOpen] = useState(false)
  const [editingImageNodeIndex, setEditingImageNodeIndex] = useState<number>()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [upgradeLimitType, setUpgradeLimitType] = useState('image')
  const imageAddedDuringAutoOpenRef = useRef(false)

  const { data: subscription } = trpc.userSubscription.get.useQuery()
  const isFreePlan = !subscription?.isSubscribed

  // Menu state
  const [isHoveringQuickmenu, setIsHoveringQuickemnu] = useState<boolean>(false)

  // Set default model based on context:
  // - generating new image → flux
  // - image selected (edit mode) → gemini (WorkspaceEditorPromptBox also auto-switches)
  useEffect(() => {
    if (isGenerateImagePressed) {
      setImageModel('flux')
    }
  }, [isGenerateImagePressed])

  // trpc

  // Brand colors for in-editor image generation (the "Inject brand colors"
  // toggle) come from the workspace's moodboard brief.
  const { data: moodboard } = trpc.moodboard.getById.useQuery(
    { id: workspace.moodboardId! },
    { enabled: !!workspace.moodboardId },
  )
  const moodboardPalette =
    moodboard?.analysisBrief?.blocks.reduce<string[]>((acc, b) => {
      if (b.type === 'palette') acc.push(...b.swatches.map((s) => s.hex))
      return acc
    }, []) ?? []
  const brandColorPalette: string[] = moodboardPalette
  const hasBrandColors = moodboardPalette.length > 0

  // Custom fonts uploaded to the workspace's moodboard become selectable in the
  // text toolbar (a "Custom fonts" group) and render on the canvas once loaded.
  const customFontDescriptors = useMemo(
    () => moodboardCustomFonts(moodboard?.items).map(({ family, url }) => ({ family, url })),
    [moodboard],
  )
  const { fonts: customFonts } = useCustomFonts(customFontDescriptors)

  const { mutateAsync: updateWorkspace } = trpc.workspace.update.useMutation()
  const { mutateAsync: getUploadUrl } = trpc.asset.getUploadUrl.useMutation()
  const { mutateAsync: deleteAssets } = trpc.asset.delete.useMutation()
  const { mutateAsync: copyAsset } = trpc.asset.copyAsset.useMutation()

  const utils = trpc.useUtils()
  /**
   * Whether an AI operation ran during this editing session.
   *
   * The save path copies `origin` / `hasSyntheticEdits` straight off the previous
   * media, which is right for a manual edit — moving a layer is not AI
   * involvement. But the editor also generates and edits images with AI, and
   * those never touched the media's provenance, so AI-editing an uploaded photo
   * in here left it reading "no AI detected" with no IPTC marker written.
   *
   * A ref rather than state: nothing renders from it, and it must not retrigger
   * the canvas on every AI call.
   */
  const aiUsedInSessionRef = useRef(false)

  const { mutateAsync: generateImage } = trpc.socialMediaPost.generateImage.useMutation()
  const { mutateAsync: editImage } = trpc.socialMediaPost.updateImage.useMutation()

  // Copy the current selection into the in-memory node clipboard. Deep-cloned so
  // later edits to the live nodes don't mutate what's on the clipboard, and taken
  // in canvas (z-order) order rather than click/selection order so paste keeps
  // the original stacking of the copied elements.
  const handleCopyNodes = () => {
    const { nodes: currentNodes, selectedNodeIndices } = nodeStore.getState()
    if (selectedNodeIndices.length === 0) return
    const orderedNodes = [...selectedNodeIndices]
      .sort((a, b) => a - b)
      .map((i) => currentNodes[i])
      .filter((n): n is LayoutNode => Boolean(n))
    if (orderedNodes.length === 0) return
    nodeClipboardStore.getState().setClipboard(
      orderedNodes.map((n) => structuredClone(n)),
      `${workspace.id}:${mediaIndex}`,
    )
    toast.success(`Copied ${orderedNodes.length} element${orderedNodes.length === 1 ? '' : 's'}`)
  }

  // Paste the clipboard into the current canvas: offset, re-group, copy each
  // image's underlying S3 asset so the pasted node owns it (survives the source
  // workspace being deleted), then append and select the new nodes.
  const handlePasteNodes = async () => {
    const { clipboardNodes, sourceCanvasKey } = nodeClipboardStore.getState()
    if (clipboardNodes.length === 0) return

    // Offset only when pasting back into the exact same canvas, so the copy
    // doesn't sit on top of the original. Pasting into any other canvas (even
    // another canvas in the same workspace) keeps the source position.
    const offset = sourceCanvasKey === `${workspace.id}:${mediaIndex}` ? 20 : 0

    // Preserve grouping across paste by mapping each old groupId to a fresh one.
    const groupIdMap = new Map<string, string>()

    const pastedNodes = await Promise.all(
      clipboardNodes.map(async (node) => {
        const clone = structuredClone(node)
        clone.x += offset
        clone.y += offset
        if (clone.groupId) {
          const remapped = groupIdMap.get(clone.groupId) ?? crypto.randomUUID()
          groupIdMap.set(clone.groupId, remapped)
          clone.groupId = remapped
        }
        if (clone.data.type === 'image' && clone.data.source.url) {
          try {
            const { url } = await copyAsset({
              sourceUrl: clone.data.source.url,
              destination: { type: 'workspace', workspaceId: workspace.id },
            })
            clone.data.source.url = url
          } catch (error) {
            // Fall back to sharing the source URL. This is safe today (canvas
            // node deletion never removes the S3 object); log for visibility.
            captureError(error)
          }
        }
        return clone
      }),
    )

    const currentNodes = nodeStore.getState().nodes
    const startIndex = currentNodes.length
    setNodes([...currentNodes, ...pastedNodes], { history: true })
    setSelectedNodeIndices(pastedNodes.map((_, i) => startIndex + i))
    toast.success(`Pasted ${pastedNodes.length} element${pastedNodes.length === 1 ? '' : 's'}`)
  }

  // Editor-wide shortcuts that must work regardless of selection: node copy/paste
  // and undo/redo. Always active; the hook's isEditableTarget guard defers to the
  // Lexical text editor while a text node is being edited.
  useKeyBindings(true, {
    copy: handleCopyNodes,
    paste: handlePasteNodes,
    undo: () => {
      if (canHistoryUndo()) historyUndo()
    },
    redo: () => {
      if (canHistoryRedo()) historyRedo()
    },
  })

  // effects

  // setup pan & zoom listeners
  useEffect(() => {
    const zoomContainer = panAndZoomRef.current
    if (!zoomContainer) return

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()

      if (e.ctrlKey || e.metaKey) {
        const { clientX, clientY } = e

        const rect = zoomContainer.getBoundingClientRect()
        if (!rect) return
        const mouseX = clientX - rect.left
        const mouseY = clientY - rect.top

        const factor = Math.pow(2, -e.deltaY * 0.003)
        const min = 0.1
        const max = 3

        const newScale = Math.max(min, Math.min(panAndZoom.scale * factor, max))

        const newX = mouseX - (mouseX - panAndZoom.x) * (newScale / panAndZoom.scale)
        const newY = mouseY - (mouseY - panAndZoom.y) * (newScale / panAndZoom.scale)

        setPanAndZoom({ scale: newScale, x: newX, y: newY })
      } else {
        // pan left / right / up / down on scroll
        const stepX = -e.deltaX
        const stepY = -e.deltaY
        const newX = panAndZoom.x + stepX
        const newY = panAndZoom.y + stepY
        setPanAndZoom({ ...panAndZoom, x: newX, y: newY })
      }
    }

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 1 || e.altKey) {
        window.addEventListener('mouseup', handleMouseUp, { once: true })
        setPanningStart({
          x: e.clientX - panAndZoom.x,
          y: e.clientY - panAndZoom.y,
        })

        e.preventDefault()
      }
    }

    const handleMouseUp = () => {
      setPanningStart(undefined)
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!panningStart) return
      setPanAndZoom((prev) => ({
        ...prev,
        x: e.clientX - panningStart.x,
        y: e.clientY - panningStart.y,
      }))
    }

    zoomContainer.addEventListener('wheel', handleWheel, { passive: false })
    zoomContainer.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mousemove', handleMouseMove)

    return () => {
      zoomContainer.removeEventListener('wheel', handleWheel)
      zoomContainer.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [panAndZoom, panningStart])

  useEffect(() => {
    if (canvasSize) {
      fitAllCanvases()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-fit when canvasSize changes, not on every render
  }, [canvasSize])

  // handlers

  const fitAllCanvases = () => {
    if (!canvasSize || !panAndZoomRef.current) return

    const panAndZoomFit = (sizeA: { x: number; y: number }, sizeB: { x: number; y: number }) => {
      const ratioA = sizeA.x / sizeA.y
      const ratioB = sizeB.x / sizeB.y

      const paddingX = 50
      const paddingY = 70

      let scale = 1

      if (ratioA > ratioB) {
        scale = (sizeB.x - paddingX * 2) / sizeA.x
      } else {
        scale = (sizeB.y - paddingY * 2) / sizeA.y
      }
      const x = (sizeB.x - sizeA.x * scale) / 2
      const y = (sizeB.y - sizeA.y * scale) / 2

      return { scale, x, y }
    }

    setPanAndZoom(
      panAndZoomFit(
        { x: canvasSize.width, y: canvasSize.height },
        { x: panAndZoomRef.current.clientWidth, y: panAndZoomRef.current.clientHeight },
      ),
    )
  }

  const handleZoomButton = (direction: 'in' | 'out') => {
    if (!canvasSize || !panAndZoomRef.current) return

    const sizeA = { x: canvasSize.width, y: canvasSize.height }
    const sizeB = {
      x: panAndZoomRef.current.clientWidth,
      y: panAndZoomRef.current.clientHeight,
    }

    setPanAndZoom((prev) => {
      const step = 0.05
      const min = 0.1
      const max = 3

      let newScale = direction === 'in' ? prev.scale + step : prev.scale - step
      newScale = Math.min(max, Math.max(min, newScale))

      // scale from Center
      const x = (sizeB.x - sizeA.x * newScale) / 2
      const y = (sizeB.y - sizeA.y * newScale) / 2

      return { ...prev, scale: newScale, x, y }
    })
  }

  const resetScale = () => {
    if (!canvasSize || !panAndZoomRef.current) return
    const viewport = {
      x: panAndZoomRef.current.clientWidth,
      y: panAndZoomRef.current.clientHeight,
    }
    setPanAndZoom({
      scale: 1,
      x: (viewport.x - canvasSize.width) / 2,
      y: (viewport.y - canvasSize.height) / 2,
    })
  }

  const handleCanvasEditingDone = async (nodes: LayoutNode[], aspectRatio: string, file: File) => {
    if (mediaIndex === null) return

    try {
      setIsWorkspaceSaving(true)

      // delete previous file from remote storage
      const previousMedia =
        workspaceMedia[mediaIndex]?.type === 'canvas' ? workspaceMedia[mediaIndex] : undefined

      if (
        previousMedia &&
        previousMedia.file.source === 'remote' &&
        previousMedia.file.key &&
        !isPlaceholderFile(previousMedia.file)
      ) {
        await deleteAssets({ keys: [previousMedia.file.key] })
      }

      // Re-stamp the flattened export with the AI marker when the canvas has AI
      // provenance — the raster loses the source images' metadata (GH #118).
      //
      // Uses the provenance this save is about to WRITE, not the one it started
      // with. Gating on the pre-session value meant AI-editing a plain upload
      // stored a row claiming AI involvement over bytes carrying no marker.
      const savedProvenance = aiUsedInSessionRef.current
        ? markAiEdited(previousMedia)
        : previousMedia
      const uploadFile = savedProvenance ? await stampCanvasProvenance(file, savedProvenance) : file

      // upload file to remote storage, update local state and db entry
      const { key, uploadUrl, assetUrl } = await getUploadUrl({
        type: 'workspace',
        workspaceId: workspace.id,
      })
      await uploadToS3(uploadUrl, uploadFile)

      const newMedia: WorkspaceMedia[] = workspaceMedia.map((m, i) =>
        i === mediaIndex
          ? {
              type: 'canvas',
              nodes,
              aspectRatio,
              backgroundColor: currentBackgroundColor,
              file: {
                source: 'remote',
                url: assetUrl,
                key,
                file: {
                  name: uploadFile.name,
                  type: uploadFile.type,
                  size: uploadFile.size,
                },
              },
              // Manual edits leave provenance alone — moving a layer is not AI
              // involvement. When AI did run this session, `markAiEdited` carries
              // the original `origin` forward rather than overwriting it.
              ...(aiUsedInSessionRef.current
                ? markAiEdited(m)
                : { origin: m.origin, hasSyntheticEdits: m.hasSyntheticEdits }),
            }
          : m,
      )

      await updateWorkspace({
        id: workspace.id,
        data: { media: newMedia },
      })

      setWorkspaceMedia(newMedia)
      onEditingDone()
    } finally {
      setIsWorkspaceSaving(false)
    }
  }

  const handleUpdateImage = async (userPrompt: string, selectedNodeIndex: number) => {
    if (selectedNodeIndex === -1) return
    const selectedNode = nodes[selectedNodeIndex]

    if (selectedNode?.data.type !== 'image') return

    try {
      const base64Img = await fetch(selectedNode.data.source.url)
        .then((r) => r.blob())
        .then(blobToBase64)
      if (typeof base64Img !== 'string') throw new Error('image or mask is not base64')

      updateNode(selectedNodeIndex, { data: { isLoading: true } })

      const result = await editImage({
        model: imageModel,
        prompt: userPrompt,
        image: base64Img,
        fluxOptions: { disablePup: false, aspect: canvasAspect },
        workspaceId: workspace.id,
      })
      utils.usageLimit.get.invalidate()
      // Only a successful call counts. A usage-limit rejection produces no
      // image, and arming the flag anyway would let a later purely manual save
      // mark media AI never touched.
      if (result.ok) aiUsedInSessionRef.current = true

      if (!result.ok) {
        setShowUpgradeModal(true)
        updateNode(selectedNodeIndex, { data: { isLoading: false } })
        return
      }

      updateNode(
        selectedNodeIndex,
        { data: { isLoading: false, source: { type: 'url', url: result.url } } },
        {
          history: true,
        },
      )
    } catch (error) {
      captureError(error, {
        flow: 'workspace-editor.updateImage',
        workspaceId: workspace.id,
        imageModel,
        canvasAspect,
      })
      const errorMessage = error instanceof Error ? error.message : String(error)
      toast.error('Failed to update image', {
        description: errorMessage.replace(/^TRPCClientError:\s*/i, ''),
        duration: 7000,
      })
      updateNode(selectedNodeIndex, { data: { isLoading: false } })
    }
  }

  const handleUpdate = async (userPrompt: string, context: Role | 'image_generation' | null) => {
    if (context === 'image') {
      if (!userPrompt.trim()) return
      setIsGeneratingImage(true)
      try {
        await handleUpdateImage(userPrompt, selectedNodeIndex)
      } finally {
        setIsGeneratingImage(false)
      }
    } else if (context === 'image_generation') {
      await handleGenerateImageFromPrompt(userPrompt)
    }
  }

  const handleGenerateImageInDialog = async (
    userPrompt: string,
    model: ImageModel,
    options?: GenerateImageOptions,
  ): Promise<{ url: string; enhancedPrompt?: string }> => {
    if (!canvasSize) throw new Error('Canvas size not available')

    setIsGeneratingImage(true)

    try {
      const captionText = workspace.captions
        ? Object.values(workspace.captions).filter(Boolean).join('\n')
        : ''

      const result = await generateImage({
        model,
        prompt: userPrompt || '',
        caption: options?.useCaption !== false ? captionText : '',
        workspaceId: workspace.id,
        fluxOptions: {
          disablePup: false,
          aspect: canvasAspect,
          steps: options?.steps,
          guidance: options?.guidance,
          resolution: options?.resolution,
        },
        colorPalette:
          options?.useColorPalette !== false
            ? brandColorPalette.length > 0
              ? brandColorPalette
              : undefined
            : undefined,
        referenceImages: options?.referenceImages,
      })
      utils.usageLimit.get.invalidate()
      // Only a successful call counts. A usage-limit rejection produces no
      // image, and arming the flag anyway would let a later purely manual save
      // mark media AI never touched.
      if (result.ok) aiUsedInSessionRef.current = true

      if (!result.ok) {
        setShowUpgradeModal(true)
        throw new Error('Usage limit exceeded')
      }

      if (result.skippedPromptEnhancement) {
        toast.warning('Prompt enhancement was skipped due to high demand', {
          description: result.promptEnhancementError,
          duration: 7000,
        })
      }

      if (result.usedModelFallback) {
        toast.info('Image generated using Gemini (Flux was unavailable)', {
          description: result.modelFallbackReason,
          duration: 7000,
        })
      }

      return { url: result.url, enhancedPrompt: result.enhancedPrompt }
    } catch (error) {
      console.error('Error generating image:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      if (!errorMessage.includes('Usage limit exceeded')) {
        toast.error('Failed to generate image', {
          description: errorMessage.replace(/^TRPCClientError:\s*/i, ''),
          duration: 7000,
        })
      }
      throw error
    } finally {
      setIsGeneratingImage(false)
    }
  }

  const handleUseGeneratedImage = (imageUrl: string) => {
    if (!canvasSize) return

    addNode({ type: 'image', url: imageUrl }, canvasSize, {
      history: true,
    })

    if (autoOpenImageDialog) {
      imageAddedDuringAutoOpenRef.current = true
    }

    toast.success('Image added to canvas!')
  }

  const handleGenerateImageFromPrompt = async (userPrompt: string) => {
    if (!canvasSize) return
    if (isGeneratingImage) return

    toast.info('Generating image...')
    setIsGeneratingImage(true)

    try {
      const result = await generateImage({
        model: imageModel,
        prompt: userPrompt || '',
        caption: '',
        workspaceId: workspace.id,
        fluxOptions: {
          disablePup: false,
          aspect: canvasAspect,
        },
        colorPalette: brandColorPalette.length > 0 ? brandColorPalette : undefined,
      })
      utils.usageLimit.get.invalidate()
      // Only a successful call counts. A usage-limit rejection produces no
      // image, and arming the flag anyway would let a later purely manual save
      // mark media AI never touched.
      if (result.ok) aiUsedInSessionRef.current = true

      if (!result.ok) {
        setShowUpgradeModal(true)
        return
      }

      addNode({ type: 'image', url: result.url }, canvasSize, {
        history: true,
      })

      if (result.skippedPromptEnhancement) {
        toast.warning('Prompt enhancement was skipped due to high demand', {
          description: result.promptEnhancementError,
          duration: 7000,
        })
      }

      if (result.usedModelFallback) {
        toast.success('Image generated using Gemini (Flux was unavailable)', {
          description: result.modelFallbackReason,
          duration: 7000,
        })
      } else if (!result.skippedPromptEnhancement) {
        toast.success('Image generated successfully!')
      }
    } catch (error) {
      captureError(error, {
        flow: 'workspace-editor.generateImage',
        workspaceId: workspace.id,
        imageModel,
        canvasAspect,
      })
      const errorMessage = error instanceof Error ? error.message : String(error)
      toast.error('Failed to generate image', {
        description: errorMessage.replace(/^TRPCClientError:\s*/i, ''),
        duration: 7000,
      })
    } finally {
      setIsGeneratingImage(false)
      setIsGenerateImagePressed(false)
    }
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canvasSize) return

    const file = event.target.files?.[0]
    if (!file) return

    const { uploadUrl, assetUrl } = await getUploadUrl({
      type: 'workspace',
      workspaceId: workspace.id,
    })

    try {
      await uploadToS3(uploadUrl, file)

      addNode({ type: 'image', url: assetUrl }, canvasSize, {
        history: true,
      })
    } catch (error) {
      captureError(error, {
        flow: 'workspace-editor.handleFileSelect',
        workspaceId: workspace.id,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      })
    }
  }

  const handleReplaceFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedNodeIndex === -1) return

    const file = event.target.files?.[0]
    if (!file) return

    try {
      const { uploadUrl, assetUrl } = await getUploadUrl({
        type: 'workspace',
        workspaceId: workspace.id,
      })

      await uploadToS3(uploadUrl, file)

      const { updateNode } = nodeStore.getState()
      updateNode(
        selectedNodeIndex,
        {
          data: { source: { type: 'url', url: assetUrl } },
        },
        { history: true },
      )

      toast.success('Image replaced!')
    } catch (error) {
      captureError(error, {
        flow: 'workspace-editor.replaceFile',
        workspaceId: workspace.id,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      })
      toast.error('Failed to replace image')
    }

    // Reset file input
    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.value = ''
    }
  }

  const handleSaveAndExit = async () => {
    if (!canvasSize || !canvasAspect) return

    try {
      const file = await capture('main')
      await handleCanvasEditingDone(nodes as LayoutNode[], canvasAspect, file)
    } catch (error) {
      captureError(error, {
        flow: 'workspace-editor.saveAndExit',
        workspaceId: workspace.id,
        canvasAspect,
      })
      const errorMessage = error instanceof Error ? error.message : String(error)
      const cleanErrorMessage = errorMessage.replace(/^TRPCClientError:\s*/i, '')
      toast.error('Error saving canvas', {
        description: cleanErrorMessage,
        duration: 7000,
      })
    }
  }

  const handleDownloadCanvas = async () => {
    try {
      const file = await capture('main')
      const downloadUrl = window.URL.createObjectURL(file)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `image-${Date.now()}.png`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(downloadUrl)
      document.body.removeChild(a)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      const cleanErrorMessage = errorMessage.replace(/^TRPCClientError:\s*/i, '')
      console.error('Error downloading content:', error)
      toast.error('Error downloading content', {
        description: cleanErrorMessage,
        duration: 7000,
      })
    }
  }

  // Logic for image pop up dialogs
  const handleOpenImageEditDialog = () => {
    if (selectedNodeIndex === -1) return
    setEditingImageNodeIndex(selectedNodeIndex)
    setIsImageEditDialogOpen(true)
  }

  const handleGenerateEditedImage = async (
    userPrompt: string,
    model: ImageModel,
    options?: GenerateImageOptions,
  ): Promise<{ url: string; enhancedPrompt?: string }> => {
    if (!canvasSize) throw new Error('Canvas size not available')
    if (editingImageNodeIndex === undefined) throw new Error('No image selected')

    setIsGeneratingImage(true)

    try {
      const selectedNode = nodes[editingImageNodeIndex]
      if (selectedNode?.data.type !== 'image') throw new Error('Selected node is not an image')

      const captionText = workspace.captions
        ? Object.values(workspace.captions).filter(Boolean).join('\n')
        : ''

      const result = await editImage({
        model,
        prompt: userPrompt,
        image: await fetch(selectedNode.data.source.url)
          .then((r) => r.blob())
          .then(blobToBase64)
          .then((base64) => {
            if (typeof base64 !== 'string') throw new Error('image is not base64')
            return base64
          }),
        fluxOptions: {
          disablePup: false,
          aspect: canvasAspect,
          steps: options?.steps,
          guidance: options?.guidance,
          resolution: options?.resolution,
        },
        workspaceId: workspace.id,
        referenceImages: options?.referenceImages,
        enhance: true,
        caption: captionText || undefined,
        colorPalette: brandColorPalette.length > 0 ? brandColorPalette : undefined,
      })
      utils.usageLimit.get.invalidate()
      // Only a successful call counts. A usage-limit rejection produces no
      // image, and arming the flag anyway would let a later purely manual save
      // mark media AI never touched.
      if (result.ok) aiUsedInSessionRef.current = true

      if (!result.ok) {
        setShowUpgradeModal(true)
        throw new Error('Usage limit exceeded')
      }

      if (result.skippedPromptEnhancement) {
        toast.warning('Prompt enhancement was skipped due to high demand', {
          description: result.promptEnhancementError,
          duration: 7000,
        })
      }

      return { url: result.url, enhancedPrompt: result.enhancedPrompt }
    } catch (error) {
      console.error('Error generating edited image:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      if (!errorMessage.includes('Usage limit exceeded')) {
        toast.error('Failed to generate edited image', {
          description: errorMessage.replace(/^TRPCClientError:\s*/i, ''),
          duration: 7000,
        })
      }
      throw error
    } finally {
      setIsGeneratingImage(false)
    }
  }

  const handleUseEditedImage = (imageUrl: string) => {
    if (editingImageNodeIndex === undefined) return

    updateNode(
      editingImageNodeIndex,
      { data: { source: { type: 'url', url: imageUrl } } },
      { history: true },
    )

    toast.success('Image updated!')
  }

  const editorCtx = useEditorContext()

  // Menu handlers - apply updates to all selected nodes
  const handleUpdateTextOptions = (options: Partial<TextOptions>, isHoverSelection?: boolean) => {
    if (selectedNodeIndices.length === 0) return

    // When a non-collapsed range is or recently was live in the canvas
    // Lexical editor, the five run-style fields apply to that range instead
    // of the whole node. Other fields (alignment, line height, padding,
    // backdrop) stay node-level. We honor `lastRange` even after the
    // contenteditable has blurred — Radix popovers portal out of the
    // toolbar wrapper, which collapses the live selection; the cached
    // range lets the click on a color swatch still hit the original span.
    if (editorCtx.editor && (editorCtx.hasRangeSelection || editorCtx.lastRange)) {
      const styleUpdate: Partial<TextOptions> = {}
      const nodeUpdate: Partial<TextOptions> = {}
      for (const key of Object.keys(options) as (keyof TextOptions)[]) {
        if (RUN_STYLE_KEYS.has(key as string)) {
          // @ts-expect-error narrowing across the TextOptions union
          styleUpdate[key] = options[key]
        } else {
          // @ts-expect-error narrowing across the TextOptions union
          nodeUpdate[key] = options[key]
        }
      }
      if (Object.keys(styleUpdate).length > 0) {
        // Apply during hover too — selection-mode previews matter as much
        // as node-mode previews (color picker drag, etc.).
        //
        // We don't call editor.focus() afterward: with the pointerdown-
        // driven commit, the editor stays in edit mode regardless of where
        // browser focus is. Refocusing would steal focus from the open
        // picker/popover and Radix would interpret it as "click outside",
        // dismissing the picker after every swatch click.
        applyStyleToSelection(
          editorCtx.editor,
          {
            fontFamily: styleUpdate.fontFamily,
            fontSize: styleUpdate.fontSize,
            fontWeight: styleUpdate.fontWeight,
            fontStyle: styleUpdate.fontStyle,
            fontColor: styleUpdate.fontColor,
          },
          editorCtx.lastRange,
        )
      }
      if (Object.keys(nodeUpdate).length > 0) {
        updateSelectedNodes({ data: { options: nodeUpdate } }, { history: !isHoverSelection })
      }
      return
    }

    updateSelectedNodes(
      { data: { options } },
      {
        history: !isHoverSelection,
      },
    )
  }

  const handleUpdateImageOptions = (options: Partial<ImageOptions>, isHoverSelection?: boolean) => {
    if (selectedNodeIndices.length === 0) return

    updateSelectedNodes(
      { data: { options } },
      {
        history: !isHoverSelection,
      },
    )
  }

  const handleUpdateShapeOptions = (options: Partial<ShapeOptions>, isHoverSelection?: boolean) => {
    if (selectedNodeIndices.length === 0) return

    updateSelectedNodes(
      { data: { options } },
      {
        history: !isHoverSelection,
      },
    )
  }

  const handleDuplicate = () => {
    if (selectedNodeIndex === -1) return

    duplicateNode(selectedNodeIndex, {
      history: true,
    })
  }

  const handleDeleteNode = () => {
    if (selectedNodeIndices.length === 0) return

    if (selectedNodeIndices.length > 1) {
      deleteSelectedNodes({ history: true })
    } else {
      deleteNode(selectedNodeIndex, { history: true })
    }
  }

  const handleToggleLock = () => {
    if (selectedNodeIndex === -1) return
    updateNode(selectedNodeIndex, { locked: !selectedNode?.locked }, { history: true })
  }

  const _handleToggleGroup = () => {
    if (selectedNodeIndices.length < 2) return

    // Check if all selected nodes are already in the same group
    const areAllGrouped =
      selectedNodes.length >= 2 &&
      selectedNodes.every((n) => n.groupId && n.groupId === selectedNodes[0]?.groupId)

    if (areAllGrouped) {
      ungroupSelectedNodes({ history: true })
    } else {
      groupSelectedNodes({ history: true })
    }
  }

  const _handleSelectAll = () => {
    setSelectedNodeIndices(nodes.map((_, i) => i))
  }

  const handleLayerChange = (
    operation: 'bringToFront' | 'moveForward' | 'moveBackward' | 'sendToBack',
  ) => {
    if (selectedNodeIndex === -1) return

    const { nodes: currentNodes } = nodeStore.getState()
    const updatedNodes = [...currentNodes]
    const currentNode = updatedNodes[selectedNodeIndex]!

    switch (operation) {
      case 'bringToFront': {
        updatedNodes.splice(selectedNodeIndex, 1)
        updatedNodes.push(currentNode)
        setSelectedNodeIndex(updatedNodes.length - 1)

        break
      }

      case 'moveForward': {
        if (selectedNodeIndex < updatedNodes.length - 1) {
          const temp = updatedNodes[selectedNodeIndex]!
          updatedNodes[selectedNodeIndex] = updatedNodes[selectedNodeIndex + 1]!
          updatedNodes[selectedNodeIndex + 1] = temp
          setSelectedNodeIndex(selectedNodeIndex + 1)
        }
        break
      }

      case 'moveBackward': {
        if (selectedNodeIndex > 0) {
          const temp = updatedNodes[selectedNodeIndex]!
          updatedNodes[selectedNodeIndex] = updatedNodes[selectedNodeIndex - 1]!
          updatedNodes[selectedNodeIndex - 1] = temp
          setSelectedNodeIndex(selectedNodeIndex - 1)
        }
        break
      }

      case 'sendToBack': {
        updatedNodes.splice(selectedNodeIndex, 1)
        updatedNodes.unshift(currentNode)
        setSelectedNodeIndex(0)
        break
      }
    }

    setNodes(updatedNodes, {
      history: true,
    })
  }

  const handleDownloadImage = async (url: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `image-${Date.now()}.png`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(downloadUrl)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error downloading image:', error)
    }
  }

  const currentCanvasMedia =
    workspaceMedia[mediaIndex]?.type === 'canvas' ? workspaceMedia[mediaIndex] : undefined
  const currentBackgroundColor: Color = currentCanvasMedia?.backgroundColor ?? {
    r: 255,
    g: 255,
    b: 255,
    a: 255,
  }

  const handleBackgroundColorChange = (color: Color) => {
    const updatedMedia = workspaceMedia.map((m, i) =>
      i === mediaIndex && m.type === 'canvas' ? { ...m, backgroundColor: color } : m,
    )
    setWorkspaceMedia(updatedMedia)
  }

  const handleImageDialogClose = () => {
    setIsImageDialogOpen(false)

    // If the dialog was auto-opened and no image was added, return to workspace
    if (autoOpenImageDialog && !imageAddedDuringAutoOpenRef.current) {
      // Remove the empty canvas that was created
      const updatedMedia = workspaceMedia.filter((_, i) => i !== mediaIndex)
      setWorkspaceMedia(updatedMedia)
      onEditingDone()
    }
  }

  // Apply a new aspect ratio (preset or custom): resize the canvas and rescale
  // every node proportionally, then persist to the canvas media item.
  const applyAspectRatio = (key: string) => {
    if (!canvasSize) return

    const newSize = calculateCanvasSize(key)

    const newNodes: LayoutNode[] = nodes.map((node) => ({
      ...node,
      x: (node.x / canvasSize.width) * newSize.width,
      y: (node.y / canvasSize.height) * newSize.height,
      width: (node.width / canvasSize.width) * newSize.width,
      height: (node.height / canvasSize.height) * newSize.height,
    }))

    const updatedMedia = workspaceMedia.map((m, i) =>
      i === mediaIndex && m.type === 'canvas' ? { ...m, aspectRatio: key, nodes: newNodes } : m,
    )

    setWorkspaceMedia(updatedMedia)
    setCanvasAspect(key)
    setCanvasSize(newSize)
    setNodes(newNodes, { history: true })
  }

  // render

  return (
    <div
      ref={panAndZoomRef}
      className="animate-fade animate-once animate-duration-500 animate-ease-in relative h-screen w-screen select-none overflow-hidden">
      {/* Resolution badge — actual exported/captured size for this device (logical × dpr) */}
      {canvasSize && (
        <div className="border-border bg-card text-muted-foreground shadow-xs absolute left-4 top-4 z-50 rounded-full border px-3 py-1.5 text-sm tabular-nums">
          {Math.round(canvasSize.width * dpr)} × {Math.round(canvasSize.height * dpr)} px
        </div>
      )}

      {/* Save and Cancel Buttons */}
      <div className="absolute right-4 top-4 z-50 flex items-center gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={isWorkspaceSaving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSaveAndExit} disabled={isWorkspaceSaving}>
          {isWorkspaceSaving ? (
            <>
              <LoadingIcon color="white" width={16} height={16} />
              Saving...
            </>
          ) : (
            <>
              <CheckMarkIcon color="white" width={16} height={16} />
              Save & Exit
            </>
          )}
        </Button>
      </div>

      {/* File input for image upload */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleFileSelect}
      />
      {/* File input for image replacement */}
      <input
        type="file"
        ref={replaceFileInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleReplaceFileSelect}
      />

      {/* General menu*/}

      <WorkspaceEditorMenuGeneral
        imageItems={{
          options: [
            { key: 'upload', label: 'Upload' },
            {
              key: 'generate',
              label: isGeneratingImage ? 'Generating...' : 'Generate',
            },
          ],
          onClick: async (key) => {
            if (!canvasSize) return
            if (isGeneratingImage) return

            switch (key) {
              case 'generate':
                setIsImageDialogOpen(true)
                break

              case 'upload':
                fileInputRef.current?.click()
                break
            }
          },
        }}
        shapeItems={{
          options: [
            {
              key: 'rectangle',
              label: 'Rectangle',
              icon: <RectangleIcon color="var(--foreground)" />,
            },
            {
              key: 'circle',
              label: 'Circle',
              icon: <CircleIcon color="var(--foreground)" />,
            },
            {
              key: 'triangle',
              label: 'Triangle',
              icon: <TriangleIcon color="var(--foreground)" />,
            },
          ],
          onClick: (key) => {
            if (!canvasSize) return

            const { addNode } = nodeStore.getState()
            addNode({ type: 'shape', shape: key }, canvasSize, {
              history: true,
            })
          },
        }}
        aspectRatioItems={{
          currentAspectRatio: canvasAspect || '1:1',
          options: ASPECT_RATIOS,
          onClick: applyAspectRatio,
          footer: (
            <AspectRatioCustomInput
              currentAspect={canvasAspect || '1:1'}
              onApply={applyAspectRatio}
            />
          ),
        }}
        backgroundColor={currentBackgroundColor}
        onBackgroundColorChange={handleBackgroundColorChange}
        colorPalette={brandColorPalette}
        onDownload={handleDownloadCanvas}
        overlayOpacity={overlayOpacity}
        onOverlayOpacityChange={setOverlayOpacity}
        onTextAdd={() => {
          if (!canvasSize) return

          const { addNode } = nodeStore.getState()
          addNode({ type: 'text', content: 'Write your text...', role: 'static' }, canvasSize, {
            history: true,
          })
        }}
        hidden={selectedNodeIndex !== -1}
      />

      {/* Context-specific menus */}
      <div
        ref={quickMenuRef}
        data-canvas-text-toolbar
        className={`absolute left-1/2 top-4 z-50 -translate-x-1/2 transition-all duration-300 ease-in-out ${
          selectedNodeIndex !== -1
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none -translate-y-4 opacity-0'
        }`}
        onMouseEnter={() => setIsHoveringQuickemnu(true)}
        onMouseLeave={() => setIsHoveringQuickemnu(false)}>
        {selectedNode && selectedNode.data.type === 'text' && (
          <WorkspaceEditorMenuText
            key={selectedNodeIndex}
            fonts={fonts}
            customFonts={customFonts}
            options={{
              ...selectedNode.data.options,
              // When a range is live, show the selection's style in the
              // toolbar instead of the node's effective style.
              ...(editorCtx.selectionStyle ?? getEffectiveTextStyle(selectedNode.data)),
            }}
            onUpdate={handleUpdateTextOptions}
            onLayerChange={handleLayerChange}
            onDelete={handleDeleteNode}
            onDuplicate={handleDuplicate}
            locked={selectedNode.locked ?? false}
            onToggleLock={handleToggleLock}
            colorPalette={brandColorPalette}
          />
        )}

        {selectedNode && selectedNode.data.type === 'image' && (
          <WorkspaceEditorMenuImage
            key={selectedNodeIndex}
            options={selectedNode.data.options}
            onUpdate={handleUpdateImageOptions}
            onLayerChange={handleLayerChange}
            onDelete={handleDeleteNode}
            onDuplicate={handleDuplicate}
            onDownload={() =>
              selectedNode.data.type === 'image' && 'url' in selectedNode.data.source
                ? handleDownloadImage(selectedNode.data.source.url)
                : undefined
            }
            onEditImage={handleOpenImageEditDialog}
            onReplaceUpload={() => replaceFileInputRef.current?.click()}
            onReplaceGenerate={() => {
              setIsImageDialogOpen(true)
            }}
            locked={selectedNode.locked ?? false}
            onToggleLock={handleToggleLock}
            colorPalette={brandColorPalette}
          />
        )}

        {selectedNode && selectedNode.data.type === 'shape' && (
          <WorkspaceEditorMenuShape
            key={selectedNodeIndex}
            options={selectedNode.data.options}
            onUpdate={handleUpdateShapeOptions}
            onLayerChange={handleLayerChange}
            onDelete={handleDeleteNode}
            onDuplicate={handleDuplicate}
            locked={selectedNode.locked ?? false}
            onToggleLock={handleToggleLock}
            colorPalette={brandColorPalette}
          />
        )}
      </div>

      {/* WORKSPACE */}
      <div ref={workspaceContainerRef} className="absolute inset-0">
        {mediaIndex !== null && workspaceMedia[mediaIndex]?.type === 'canvas' && (
          <WorkspaceEditorCanvas
            panAndZoom={panAndZoom}
            canvasMedia={workspaceMedia[mediaIndex]}
            backgroundColor={currentBackgroundColor}
            overlayOpacity={overlayOpacity}
            onCanvasSizeChange={setCanvasSize}
            onCanvasAspectChange={setCanvasAspect}
            inputButtonRef={inputButtonRef}
            isHoveringQuickmenu={isHoveringQuickmenu}
            setIsHoveringQuickmenu={setIsHoveringQuickemnu}
            quickMenuRef={quickMenuRef}
          />
        )}
      </div>

      <WorkspaceEditorPromptBox
        inputButtonRef={inputButtonRef}
        selectedNode={selectedNode}
        isGenerateImagePressed={isGenerateImagePressed}
        isGeneratingImage={isGeneratingImage}
        onBlur={() => setIsGenerateImagePressed(false)}
        onSubmit={handleUpdate}
        imageModel={imageModel}
        onModelChange={setImageModel}
        isFreePlan={isFreePlan}
        onUpgradeClick={(limit) => {
          setUpgradeLimitType(limit)
          setShowUpgradeModal(true)
        }}
      />

      <WorkspaceEditorPopupNew
        isOpen={isImageDialogOpen}
        onClose={handleImageDialogClose}
        onGenerate={handleGenerateImageInDialog}
        onUseImage={handleUseGeneratedImage}
        isGenerating={isGeneratingImage}
        hasBrandColors={hasBrandColors}
        isFreePlan={isFreePlan}
        onUpgradeClick={(limit) => {
          setUpgradeLimitType(limit)
          setShowUpgradeModal(true)
        }}
      />

      <WorkspaceEditorPopupEdit
        isOpen={isImageEditDialogOpen}
        currentImageUrl={
          editingImageNodeIndex !== undefined && nodes[editingImageNodeIndex]?.data.type === 'image'
            ? nodes[editingImageNodeIndex].data.source.url
            : ''
        }
        onClose={() => {
          setIsImageEditDialogOpen(false)
          setEditingImageNodeIndex(undefined)
        }}
        onGenerate={handleGenerateEditedImage}
        onUseImage={handleUseEditedImage}
        isGenerating={isGeneratingImage}
        isFreePlan={isFreePlan}
        onUpgradeClick={(limit) => {
          setUpgradeLimitType(limit)
          setShowUpgradeModal(true)
        }}
      />

      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limitType={upgradeLimitType}
        surface="workspace_editor"
      />

      {/* BOTTOM BAR */}
      <WorkspaceEditorBottomBar
        isOpen={isOpen}
        scale={panAndZoom.scale}
        onZoomIn={() => handleZoomButton('in')}
        onZoomOut={() => handleZoomButton('out')}
        onResetScale={resetScale}
        onFitAll={fitAllCanvases}
        onUndo={historyUndo}
        onRedo={historyRedo}
        canUndo={canHistoryUndo()}
        canRedo={canHistoryRedo()}
      />
    </div>
  )
}
